/**
 * AgentMessage — clean document-stream chat.
 *
 * Font: Segoe UI / Calibri stack throughout.
 * No bubbles. User right, agent left. Thinking is hidden — only the final
 * response text is shown. Blank lines in LLM output are collapsed so the
 * response body stays compact.
 */

import {
  ArrowRight,
  Briefcase,
  ChevronDown,
  ChevronRight,
  Copy,
  FileText,
  GraduationCap,
  Mail,
  PenLine,
  Search,
  Sparkles,
  UserCheck,
  Volume2,
  VolumeX,
  Wand2,
  Wrench,
  Zap,
} from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { AgentRole } from '../types';
import { useTranslatedContent } from '../hooks/useTranslatedContent';
import { localizeActionLabel } from '../utils/agentWorkflowI18n';
import { STATUS_TAG_RE } from '../utils/agentStatusTokens';
import { AgentStatusInline } from './AgentStatusInline';
import { StreamCaret } from './StreamCaret';

// ─── Public interfaces ────────────────────────────────────────────────────────

export interface AgentActionButton {
  id: string;
  label: string;
  variant?: 'primary' | 'secondary' | 'danger';
  onClick: () => void;
}

export interface AgentExecutionStep {
  id: string;
  label: string;
  status: 'pending' | 'active' | 'completed';
  content?: React.ReactNode;
}

export interface AgentMessageProps {
  role:             AgentRole;
  content:          string;
  /** Stable id for translation cache (message id). */
  messageId?:       string;
  userName?:        string;
  isTurnEnd?:       boolean;
  isStreaming?:     boolean;
  isProcessing?:    boolean;
  thinkingMs?:      number;
  modelLabel?:      string;
  thinking?:        string;
  widget?:          React.ReactNode;
  actions?:         AgentActionButton[];
  executionSteps?:  AgentExecutionStep[];
  onEdit?:          (text: string) => void;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const CHAT_FONT = 'Calibri, "Segoe UI", Arial, sans-serif';
const ACTION_TAG_RE = /\{"action"\s*:\s*"[^"]*"[^}]*\}?/g;

function resolveActionIcon(label: string, id: string): React.FC<{ className?: string }> {
  const lower = `${label} ${id}`.toLowerCase();
  if (lower.includes('optimize') || lower.includes('resume')) return FileText;
  if (lower.includes('job') || lower.includes('matching') || lower.includes('similar')) return Briefcase;
  if (lower.includes('cover letter') || lower.includes('cover')) return PenLine;
  if (lower.includes('application') || lower.includes('email') || lower.includes('mail')) return Mail;
  if (lower.includes('interview') || lower.includes('prep')) return GraduationCap;
  if (lower.includes('profile') || lower.includes('complete')) return UserCheck;
  if (lower.includes('build') || lower.includes('create')) return Wand2;
  if (lower.includes('dashboard') || lower.includes('track')) return Search;
  if (lower.includes('download')) return FileText;
  return Sparkles;
}

function hashContent(text: string): string {
  let h = 0;
  for (let i = 0; i < text.length; i++) {
    h = (Math.imul(31, h) + text.charCodeAt(i)) | 0;
  }
  return Math.abs(h).toString(36);
}

// ─── Strip <think>…</think> from a finished response string ──────────────────

function stripThinkBlocks(text: string): string {
  return text.replace(/<think>[\s\S]*?<\/think>/gi, '').replace(ACTION_TAG_RE, '').trim();
}

// ─── Compact markdown renderer ────────────────────────────────────────────────
// Empty lines are dropped (no h-2 spacers) — prevents the LLM's double-spaced
// output from creating huge gaps.

function renderMarkdown(text: string): React.ReactNode {
  if (!text) return null;
  const nodes: React.ReactNode[] = [];

  text.split('\n').forEach((line, li) => {
    if (!line.trim()) return;                                // collapse blank lines

    const bullet = line.match(/^[-*•]\s+(.*)/);
    if (bullet) { nodes.push(<li key={li} className="ml-4 list-disc">{inlineMd(bullet[1])}</li>); return; }

    const num = line.match(/^(\d+)\.\s+(.*)/);
    if (num)    { nodes.push(<li key={li} className="ml-4 list-decimal">{inlineMd(num[2])}</li>); return; }

    const h3 = line.match(/^###\s+(.*)/);
    if (h3)     { nodes.push(<p key={li} className="agent-chat-heading mt-2 font-semibold">{inlineMd(h3[1])}</p>); return; }

    const h2 = line.match(/^##\s+(.*)/);
    if (h2)     { nodes.push(<p key={li} className="agent-chat-heading mt-2 font-semibold">{inlineMd(h2[1])}</p>); return; }

    const h1 = line.match(/^#\s+(.*)/);
    if (h1)     { nodes.push(<p key={li} className="agent-chat-heading agent-chat-heading--lg mt-2 font-semibold">{inlineMd(h1[1])}</p>); return; }

    nodes.push(<p key={li}>{inlineMd(line)}</p>);
  });

  return <>{nodes}</>;
}

function inlineMd(text: string): React.ReactNode {
  const parts: React.ReactNode[] = [];
  const re = /(\*\*(.+?)\*\*|\*(.+?)\*|`(.+?)`|\[([^\]]+)\]\(([^)]+)\))/g;
  let last = 0, m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    if (m[2])       parts.push(<strong key={m.index}>{m[2]}</strong>);
    else if (m[3])  parts.push(<em key={m.index}>{m[3]}</em>);
    else if (m[4])  parts.push(<code key={m.index} className="agent-chat-code">{m[4]}</code>);
    else if (m[5] && m[6]) {
      const href = m[6];
      const isNonUrl = /^(agent|action|click|button)$/i.test(href.trim());
      if (isNonUrl) {
        parts.push(<strong key={m.index}>{m[5]}</strong>);
      } else if (href.startsWith('job://')) {
        const jobId = href.slice(6);
        parts.push(
          <a
            key={m.index}
            href="#"
            className="agent-chat-link font-semibold"
            onClick={(e) => {
              e.preventDefault();
              window.dispatchEvent(new CustomEvent('agent:open-job', { detail: { jobId } }));
            }}
          >
            {m[5]}
          </a>,
        );
      } else
      if (href.startsWith('/') && !href.startsWith('//')) {
        parts.push(
          <a
            key={m.index}
            href={href}
            className="agent-chat-link"
            onClick={(e) => {
              e.preventDefault();
              window.history.pushState({}, '', href);
              window.dispatchEvent(new PopStateEvent('popstate'));
            }}
          >
            {m[5]}
          </a>,
        );
      } else {
        parts.push(
          <a key={m.index} href={href} target="_blank" rel="noopener noreferrer" className="agent-chat-link">{m[5]}</a>,
        );
      }
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.length === 1 && typeof parts[0] === 'string' ? parts[0] : <>{parts}</>;
}

// ─── Agent identity row ───────────────────────────────────────────────────────
// The dot is absolutely positioned so it can serve as a stop on the vertical
// timeline rail rendered by the assistant turn wrapper.

function AgentRow({ model, dotActive = false }: { model?: string; dotActive?: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="mb-2 flex items-center gap-2 relative">
      <span
        className="inline-flex h-[20px] w-[20px] items-center justify-center rounded-full text-white relative z-10"
        style={{
          background: 'var(--ds-brand)',
          boxShadow: dotActive ? '0 0 0 2px var(--ds-brand-soft-2)' : undefined,
        }}
      >
        <Sparkles className="h-3 w-3" />
      </span>
      <span className="text-[15px] font-semibold" style={{ color: 'var(--ds-text-strong)' }}>
        {t('agent.chrome.agentName', { defaultValue: 'GoGrabJob Agent' })}
      </span>
      {model && (() => {
        const isPublicBrand = /^(GoGrabJob AI|Advanced AI|Fast AI|Research AI)$/i.test(model.trim());
        const isOllama = !isPublicBrand && !model.startsWith('groq/') && !model.startsWith('groq-');
        return (
          <span
            className={`agent-chat-model-badge ${isOllama ? 'is-local' : 'is-cloud'}`}
            title={isPublicBrand ? model : isOllama ? `Local · ${model}` : `Cloud · ${model}`}
          >
            <span
              className="w-1.5 h-1.5 rounded-full shrink-0"
              style={{ background: isOllama ? '#10b981' : '#f59e0b' }}
            />
            {model}
          </span>
        );
      })()}
    </div>
  );
}

// ─── Action tool chip ─────────────────────────────────────────────────────────

function ActionChip({ content }: { content: string }) {
  const { t } = useTranslation();
  const low = content.toLowerCase();
  if (low.includes('job_search') || low.includes('job search'))
    return <span className="agent-chat-tool-note"><Search className="mr-1 inline h-3.5 w-3.5" />{t('agent.status.searchingJobs', { defaultValue: 'Searching jobs…' })}</span>;
  if (low.includes('web_search') || low.includes('web search'))
    return <span className="agent-chat-tool-note"><Search className="mr-1 inline h-3.5 w-3.5" />{t('agent.status.searchingWeb', { defaultValue: 'Searching web…' })}</span>;
  if (low.includes('optimize') || low.includes('score'))
    return <span className="agent-chat-tool-note"><Zap className="mr-1 inline h-3.5 w-3.5" />{t('agent.status.optimizingResume', { defaultValue: 'Optimizing resume…' })}</span>;
  return <span className="agent-chat-tool-note"><Wrench className="mr-1 inline h-3.5 w-3.5" />{content.slice(0, 60)}</span>;
}

// ─── Copy button ──────────────────────────────────────────────────────────────

function CopyBtn({ text }: { text: string }) {
  const [ok, setOk] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => {
        try { await navigator.clipboard.writeText(text); setOk(true); setTimeout(() => setOk(false), 1800); } catch { /* noop */ }
      }}
      className="agent-chat-copy inline-flex items-center gap-1 text-[12px] transition-colors"
    >
      <Copy className="h-3 w-3" />
      {ok ? 'Copied' : 'Copy'}
    </button>
  );
}

// ─── Speak button (browser SpeechSynthesis) ───────────────────────────────────

function SpeakBtn({ text }: { text: string }) {
  const [speaking, setSpeaking] = useState(false);

  const handleSpeak = () => {
    if (typeof window === 'undefined' || !window.speechSynthesis) return;
    if (speaking) {
      window.speechSynthesis.cancel();
      setSpeaking(false);
      return;
    }
    const plain = text.replace(/[*_#>`~\[\](){}|\\]/g, '').replace(/\n{2,}/g, '. ').trim();
    if (!plain) return;
    const utt = new SpeechSynthesisUtterance(plain.slice(0, 3000));
    utt.rate = 1;
    utt.pitch = 1;
    const voices = window.speechSynthesis.getVoices();
    const preferred = voices.find(v => /female|zira|samantha|google.*female/i.test(v.name) && v.lang.startsWith('en'));
    if (preferred) utt.voice = preferred;
    utt.onend = () => setSpeaking(false);
    utt.onerror = () => setSpeaking(false);
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utt);
    setSpeaking(true);
  };

  if (typeof window === 'undefined' || !window.speechSynthesis) return null;

  return (
    <button
      type="button"
      onClick={handleSpeak}
      className="agent-chat-copy inline-flex items-center gap-1 text-[12px] transition-colors"
      title={speaking ? 'Stop speaking' : 'Listen'}
    >
      {speaking ? <VolumeX className="h-3 w-3" /> : <Volume2 className="h-3 w-3" />}
      {speaking ? 'Stop' : 'Listen'}
    </button>
  );
}

// ─── Reasoning disclosure ─────────────────────────────────────────────────────

function ReasoningDisclosure({ text }: { text: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  if (!text) return null;
  return (
    <div className="mb-2">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="agent-chat-copy flex items-center gap-1 text-[12px] transition-colors"
      >
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        <span>{t('agent.chrome.viewReasoning', { defaultValue: 'View reasoning' })}</span>
      </button>
      {open && (
        <pre className="agent-chat-reasoning mt-1.5 max-h-40 overflow-y-auto whitespace-pre-wrap rounded-md p-2.5 font-mono text-[11px] leading-5">
          {text}
        </pre>
      )}
    </div>
  );
}

// ─── Initiating skeleton ──────────────────────────────────────────────────────

function ThinkingSkeleton({ model }: { model?: string }) {
  return (
    <div
      className="relative mx-auto w-full max-w-[760px] py-3"
      style={{ fontFamily: CHAT_FONT }}
      aria-live="polite"
      aria-label="Agent is responding"
    >
      <span
        aria-hidden
        className="agent-chat-rail absolute"
        style={{ left: '9px', top: '34px', bottom: '4px', width: '2px', borderRadius: '1px' }}
      />
      <AgentRow model={model} dotActive />
      <div className="space-y-1.5 pl-[26px]">
        <div className="agent-chat-skeleton h-3 w-40 animate-pulse rounded" />
        <div className="agent-chat-skeleton h-3 w-64 animate-pulse rounded" />
        <div className="agent-chat-skeleton h-3 w-52 animate-pulse rounded" />
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const AgentMessage: React.FC<AgentMessageProps> = ({
  role,
  content,
  messageId,
  isTurnEnd = false,
  isStreaming = false,
  isProcessing = false,
  modelLabel,
  thinking,
  widget,
  actions,
  executionSteps,
  onEdit,
}) => {
  const { t } = useTranslation();

  const visibleContent = useMemo<string>(() => {
    if (role !== 'assistant') return content;
    if (isStreaming) {
      return content
        .replace(/<think>[\s\S]*/i, '')
        .replace(STATUS_TAG_RE, '')
        .replace(ACTION_TAG_RE, '')
        .trim();
    }
    return thinking != null ? stripThinkBlocks(content) : stripThinkBlocks(content);
  }, [content, isStreaming, role, thinking]);

  const { text: displayContent, isTranslating } = useTranslatedContent(
    visibleContent,
    messageId ?? `msg-${hashContent(visibleContent)}`,
    {
      enabled: role === 'assistant',
      isStreaming,
      context: 'agent assistant conversation',
    },
  );

  const { text: displayUserContent, isTranslating: isUserTranslating } = useTranslatedContent(
    content,
    messageId ?? `user-${hashContent(content)}`,
    {
      enabled: role === 'user',
      context: 'agent user message',
    },
  );

  // ── User turn ──
  if (role === 'user') {
    return (
      <div
        className={`agent-chat-turn agent-chat-turn--user group mx-auto flex w-full max-w-[760px] flex-col items-end py-3 ${isTurnEnd ? 'agent-chat-turn--divider' : ''}`}
        style={{ fontFamily: CHAT_FONT }}
      >
        <div className="agent-chat-user-block flex w-full max-w-[80%] flex-col items-end">
          <p className="agent-chat-user-label mb-1.5 text-[13px] font-medium">
            {t('agent.chrome.you', { defaultValue: 'You' })}
          </p>
          <div className="agent-chat-user-bubble w-full rounded-2xl rounded-tr-sm px-4 py-3 text-left">
            <p className="agent-chat-user-body text-[16px] font-medium leading-[1.6] whitespace-pre-wrap break-words">
              {displayUserContent}
            </p>
          </div>
          {isUserTranslating && (
            <p className="t-tag t-muted mt-1 text-left text-[12px]">
              {t('agent.message.translating', { defaultValue: 'Translating…' })}
            </p>
          )}
          <div className="pointer-events-none mt-1.5 flex gap-2 opacity-0 transition-opacity group-hover:pointer-events-auto group-hover:opacity-100">
            <CopyBtn text={displayUserContent} />
            {onEdit && (
              <button type="button" onClick={() => onEdit(content)}
                className="agent-chat-copy text-[12px] transition-colors">
                Edit
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Tool note ──
  if (role === 'tool') {
    return (
      <div
        className={`agent-chat-turn mx-auto w-full max-w-[760px] py-2 ${isTurnEnd ? 'agent-chat-turn--divider' : ''}`}
        style={{ fontFamily: CHAT_FONT }}
      >
        <ActionChip content={content} />
      </div>
    );
  }

  // ── Assistant turn ──

  // Final reasoning text shown only when done
  const finalReasoning = !isStreaming ? (thinking ?? '') : '';

  const showThinkingTimeline = Boolean(executionSteps && executionSteps.length > 0);
  const isInitiating = isStreaming && !visibleContent && !showThinkingTimeline;
  const showCaret    = isStreaming && !isInitiating && !showThinkingTimeline;
  const canCopy      = !isStreaming && !isProcessing && visibleContent.length > 0;

  if (isInitiating) {
    return <ThinkingSkeleton model={modelLabel} />;
  }

  return (
    <div
      className={`agent-chat-turn group relative mx-auto w-full max-w-[760px] py-3 ${isTurnEnd ? '' : ''}`}
      style={{ fontFamily: CHAT_FONT }}
    >
      {/* Vertical timeline rail — runs from below the dot to the next agent step.
          Hidden on the very last assistant turn so we don't leave a trailing line. */}
      {!isTurnEnd && (
        <span
          aria-hidden
          className="agent-chat-rail absolute"
          style={{
            left: '9px',
            top: '34px',
            bottom: '4px',
            width: '2px',
            borderRadius: '1px',
          }}
        />
      )}

      <AgentRow model={modelLabel} dotActive={isStreaming || isProcessing} />

      <div className="pl-[26px]">
        {/* Execution steps — single inline status line (no vertical stack) */}
        {showThinkingTimeline && executionSteps && executionSteps.length > 0 && (
          <AgentStatusInline steps={executionSteps} />
        )}

        {/* Collapsed reasoning — only visible after streaming finishes */}
        {finalReasoning && <ReasoningDisclosure text={finalReasoning} />}

        {/* Main response body */}
        <div className="agent-chat-body space-y-1.5 text-[16px] leading-[1.7]">
          {renderMarkdown(displayContent)}
          {isTranslating && (
            <p className="t-tag t-muted text-[12px]">
              {t('agent.message.translating', { defaultValue: 'Translating…' })}
            </p>
          )}
          {showCaret && <StreamCaret />}
        </div>

        {isProcessing && !showThinkingTimeline && (
          <div className="mt-2 flex gap-1">
            {[0, 1, 2].map(i => (
              <span key={i}
                className="h-1.5 w-1.5 animate-bounce rounded-full bg-indigo-400"
                style={{ animationDelay: `${i * 120}ms` }} />
            ))}
          </div>
        )}

        {widget}

        {actions && actions.length > 0 && (
          <div className="agent-engagement-cards mt-4 flex flex-wrap gap-2">
            {actions.map((a, i) => {
              const isDanger = a.variant === 'danger';
              const isPrimary = !isDanger && (a.variant === 'primary' || i === 0);
              const ActionIcon = resolveActionIcon(a.label, a.id);
              return (
                <button
                  key={a.id}
                  type="button"
                  onClick={a.onClick}
                  className={[
                    'agent-engagement-chip group',
                    isPrimary ? 'agent-engagement-chip--primary' : '',
                    isDanger ? 'agent-engagement-chip--danger' : '',
                  ].filter(Boolean).join(' ')}
                >
                  <ActionIcon className="h-4 w-4 shrink-0 opacity-80" />
                  <span>{localizeActionLabel(a.label)}</span>
                  <ArrowRight className="h-3.5 w-3.5 shrink-0 opacity-0 -translate-x-1 transition-all group-hover:opacity-70 group-hover:translate-x-0" />
                </button>
              );
            })}
          </div>
        )}

        {canCopy && (
          <div className="mt-2 flex gap-3 opacity-60 transition-opacity hover:opacity-100">
            <CopyBtn text={displayContent} />
            <SpeakBtn text={displayContent} />
          </div>
        )}
      </div>
    </div>
  );
};

AgentMessage.displayName = 'AgentMessage';
export default AgentMessage;
